import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { FileText, Shield, Users, MessageSquare, X, MapPin, Bell, PenLine, CalendarDays, BookUser, BookOpen, BarChart3, FolderKanban } from 'lucide-react';
import DailyReportPDF from '@/components/DailyReportPDF';
import RondeReportPDF from '@/components/RondeReportPDF';
import AgentControlPanel from '@/components/AgentControlPanel';
import CalendarWidget, { getParisToday, CALENDAR_CHANGED_EVENT } from '@/components/CalendarWidget';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Dashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [sites, setSites] = useState([]);
  const [todayCalendarEvents, setTodayCalendarEvents] = useState([]);
  const [nextEvent, setNextEvent] = useState(null);
  const [todayReportSite, setTodayReportSite] = useState(null);
  const [todayReport, setTodayReport] = useState(null);
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [notifications, setNotifications] = useState({ unreadMessages: 0, consignesToSign: 0 });
  const [todayEvents, setTodayEvents] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showSitesModal, setShowSitesModal] = useState(false);

  useEffect(() => {
    loadDashboardData();
    loadTodayEvents();
  }, []);

  // Le calendrier (fenêtre ci-dessous) prévient dès qu'un rendez-vous est
  // créé, modifié ou supprimé : on recharge les rendez-vous du jour aussitôt.
  useEffect(() => {
    window.addEventListener(CALENDAR_CHANGED_EVENT, loadTodayEvents);
    return () => window.removeEventListener(CALENDAR_CHANGED_EVENT, loadTodayEvents);
    // eslint-disable-next-line
  }, []);

  const loadTodayReport = async (site) => {
    const token = localStorage.getItem('token');
    const todayStr = getParisToday();
    try {
      const res = await fetch(`${BACKEND_URL}/api/daily-reports?site_id=${site.id}&start_date=${todayStr}&end_date=${todayStr}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTodayReportSite(site);
        setTodayReport(data.length > 0 ? data[0] : null);
      }
    } catch { /* silencieux */ }
  };

  const loadTodayEvents = async () => {
    const token = localStorage.getItem('token');
    const todayStr = getParisToday();
    const monthStr = todayStr.slice(0, 7);
    // Mois suivant, au cas où aucun événement n'est prévu avant la fin du mois en cours
    const [y, m] = monthStr.split('-').map(Number);
    const nextMonthDate = new Date(y, m, 1); // m est déjà "mois suivant" en index 0-based
    const nextMonthStr = `${nextMonthDate.getFullYear()}-${String(nextMonthDate.getMonth() + 1).padStart(2, '0')}`;

    const currentTime = new Intl.DateTimeFormat('fr-FR', {
      timeZone: 'Europe/Paris', hour: '2-digit', minute: '2-digit', hour12: false
    }).format(new Date()).replace('h', ':');

    try {
      const [resCurrent, resNext] = await Promise.all([
        fetch(`${BACKEND_URL}/api/calendar-events?month=${monthStr}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${BACKEND_URL}/api/calendar-events?month=${nextMonthStr}`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      const currentEvents = resCurrent.ok ? await resCurrent.json() : [];
      const nextEvents = resNext.ok ? await resNext.json() : [];
      const all = [...currentEvents, ...nextEvents];

      setTodayCalendarEvents(currentEvents.filter(ev => ev.date === todayStr));

      // Le tout prochain événement : après aujourd'hui, ou plus tard aujourd'hui
      const upcoming = all
        .filter(ev => ev.date > todayStr || (ev.date === todayStr && (!ev.time || ev.time >= currentTime)))
        .sort((a, b) => (a.date + (a.time || '00:00')).localeCompare(b.date + (b.time || '00:00')));
      setNextEvent(upcoming[0] || null);
    } catch { /* silencieux */ }
  };

  const loadDashboardData = async () => {
    const token = localStorage.getItem('token');
    try {
      const [statsRes, sitesRes, notifRes, eventsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/statistics/overview`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${BACKEND_URL}/api/sites`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${BACKEND_URL}/api/notifications/count`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${BACKEND_URL}/api/events/today`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }

      if (sitesRes.ok) {
        const sitesData = await sitesRes.json();
        setSites(sitesData);
        if (sitesData.length > 0) {
          const canViewReport = user?.permission_overrides?.view_daily_report ?? ['admin'].includes(user?.role);
          if (canViewReport) loadTodayReport(sitesData[0]);
        }
      }

      // Get notifications from dedicated endpoint
      if (notifRes.ok) {
        const notifData = await notifRes.json();
        setNotifications({
          consignesToSign: notifData.consignes_to_sign || 0,
          unreadMessages: notifData.unread_messages || 0
        });
      }

      // Get today's events count
      if (eventsRes.ok) {
        const eventsData = await eventsRes.json();
        setTodayEvents(eventsData.count || 0);
      }
    } catch (error) {
      toast.error('Erreur de chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const menuItems = [
    {
      title: 'Main Courante',
      description: user?.role === 'client' ? 'Consulter les événements' : 'Consulter et créer des événements',
      icon: FileText,
      path: '/main-courante',
      color: 'bg-blue-500'
    },
    {
      title: 'Consignes',
      description: 'Accéder aux consignes de sécurité',
      icon: BookOpen,
      path: '/consignes',
      color: 'bg-green-500'
    }
  ];

  // Messagerie: pas pour le rôle "site" (déjà couvert ailleurs par les permissions détaillées)
  if (user?.role !== 'site') {
    menuItems.push({
      title: 'Messagerie',
      description: 'Échanger avec l\'équipe',
      icon: MessageSquare,
      path: '/messagerie',
      color: 'bg-purple-500'
    });
  }

  menuItems.push({
    title: 'Organigramme',
    description: 'Découvrir l\'équipe de votre site',
    icon: Users,
    path: '/trombinoscope',
    color: 'bg-teal-500'
  });

  const canViewDirectory = user?.permission_overrides?.view_directory ?? true;
  if (canViewDirectory) {
    menuItems.push({
      title: 'Annuaire',
      description: 'Contacts utiles du site',
      icon: BookUser,
      path: '/annuaire',
      color: 'bg-cyan-500'
    });
  }

  const canWriteAgentReports = user?.permission_overrides?.write_agent_reports ?? ['admin', 'chef_poste', 'agent', 'site'].includes(user?.role);
  const canReadAgentReports = user?.permission_overrides?.read_agent_reports ?? ['admin', 'chef_poste', 'agent'].includes(user?.role);
  if (canWriteAgentReports || canReadAgentReports) {
    menuItems.push({
      title: 'Comptes rendus',
      description: 'Rédiger un compte rendu d\'intervention',
      icon: PenLine,
      path: '/comptes-rendus',
      color: 'bg-pink-500'
    });
  }

  const canViewDocTerrain = user?.permission_overrides?.view_doc_terrain ?? ['admin', 'chef_poste'].includes(user?.role);
  if (canViewDocTerrain) {
    menuItems.push({
      title: 'Doc Terrain',
      description: 'Fiches d\'intervention terrain (SST, etc.)',
      icon: FolderKanban,
      path: '/doc-terrain',
      color: 'bg-rose-500'
    });
  }

  const formatUpcoming = (ev) => {
    const todayStr = getParisToday();
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowStr = new Intl.DateTimeFormat('fr-FR', {
      timeZone: 'Europe/Paris', year: 'numeric', month: '2-digit', day: '2-digit'
    }).formatToParts(tomorrow).reduce((acc, p) => { acc[p.type] = p.value; return acc; }, {});
    const tomorrowFormatted = `${tomorrowStr.year}-${tomorrowStr.month}-${tomorrowStr.day}`;

    let when;
    if (ev.date === todayStr) when = "aujourd'hui";
    else if (ev.date === tomorrowFormatted) when = 'demain';
    else when = new Date(ev.date + 'T12:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });

    return `${ev.title} — ${when}${ev.time ? ` à ${ev.time}` : ''}`;
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-4 animate-fade-in" data-testid="dashboard">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-slate-100 mb-1" style={{ fontFamily: 'Space Grotesk' }}>
            Tableau de bord
          </h1>
          <p className="text-gray-600 dark:text-slate-300">Bienvenue, {user?.name}</p>
        </div>

        {/* Stats Overview */}
        {stats && (
          <div className={`grid grid-cols-1 gap-3 ${user?.role === 'site' ? 'md:grid-cols-2' : 'md:grid-cols-3'}`}>
            <Card
              className="card-hover cursor-pointer bg-gradient-to-br from-white to-blue-50/50 dark:from-slate-800 dark:to-slate-800/80 border-0 shadow-lg"
              onClick={() => navigate('/main-courante')}
              data-testid="card-total-events"
            >
              <CardHeader className="flex flex-row items-center justify-between pb-1 p-4">
                <CardTitle className="text-sm font-medium text-slate-600 dark:text-slate-300">Événements du jour</CardTitle>
                <div className="w-8 h-8 rounded-xl bg-blue-100 flex items-center justify-center">
                  <FileText className="h-4 w-4 text-blue-600" />
                </div>
              </CardHeader>
              <CardContent className="p-4 pt-0">
                <div className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent">{todayEvents}</div>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Cliquez pour voir le listing</p>
              </CardContent>
            </Card>

            {/* Sites assignés : sans intérêt pour un compte "Site", qui est de
                toute façon verrouillé sur son unique site - masqué pour ce rôle. */}
            {user?.role !== 'site' && (
              <Card
                className="card-hover cursor-pointer bg-gradient-to-br from-white to-emerald-50/50 dark:from-slate-800 dark:to-slate-800/80 border-0 shadow-lg"
                onClick={() => setShowSitesModal(true)}
                data-testid="card-sites-assigned"
              >
                <CardHeader className="flex flex-row items-center justify-between pb-1 p-4">
                  <CardTitle className="text-sm font-medium text-slate-600 dark:text-slate-300">Sites Assignés</CardTitle>
                  <div className="w-8 h-8 rounded-xl bg-emerald-100 flex items-center justify-center">
                    <Shield className="h-4 w-4 text-emerald-600" />
                  </div>
                </CardHeader>
                <CardContent className="p-4 pt-0">
                  <div className="text-2xl font-bold bg-gradient-to-r from-emerald-600 to-emerald-800 bg-clip-text text-transparent">{sites.length}</div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Cliquez pour voir la liste</p>
                </CardContent>
              </Card>
            )}

            <Card className="bg-gradient-to-br from-white to-slate-50/50 dark:from-slate-800 dark:to-slate-800/80 border-0 shadow-lg" data-testid="card-notifications">
              <CardHeader className="flex flex-row items-center justify-between pb-1 p-4">
                <CardTitle className="text-sm font-medium text-slate-600 dark:text-slate-300">Notifications</CardTitle>
                <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${(notifications.unreadMessages + notifications.consignesToSign) > 0 ? 'bg-orange-100' : 'bg-slate-100 dark:bg-slate-700'}`}>
                  <Bell className={`h-4 w-4 ${(notifications.unreadMessages + notifications.consignesToSign) > 0 ? 'text-orange-600 animate-pulse' : 'text-slate-400 dark:text-slate-500'}`} />
                </div>
              </CardHeader>
              <CardContent className="space-y-1.5 p-4 pt-0">
                {/* Consignes à signer - clickable row (masqué pour les comptes qui ne signent pas) */}
                {(user?.permission_overrides?.sign_consignes ?? ['admin', 'chef_poste', 'agent'].includes(user?.role)) && (
                <div 
                  className={`flex items-center justify-between p-2 rounded-xl cursor-pointer transition-all ${
                    notifications.consignesToSign > 0 
                      ? 'bg-gradient-to-r from-orange-50 to-amber-50 hover:from-orange-100 hover:to-amber-100 border border-orange-200 shadow-sm' 
                      : 'hover:bg-slate-50 dark:bg-slate-800 border border-transparent'
                  }`}
                  onClick={() => navigate('/consignes')}
                  data-testid="notif-consignes"
                >
                  <span className={`text-sm flex items-center gap-2 ${notifications.consignesToSign > 0 ? 'text-orange-700 font-semibold' : 'text-slate-600 dark:text-slate-300'}`}>
                    <PenLine className={`h-4 w-4 ${notifications.consignesToSign > 0 ? 'text-orange-600' : 'text-slate-400 dark:text-slate-500'}`} />
                    Consignes à signer
                  </span>
                  <span className={`text-sm font-bold px-2 py-0.5 rounded-lg ${
                    notifications.consignesToSign > 0 
                      ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-sm' 
                      : 'text-slate-400 dark:text-slate-500'
                  }`}>
                    {notifications.consignesToSign}
                  </span>
                </div>
                )}
                
                {/* Messages non lus - clickable row */}
                <div 
                  className={`flex items-center justify-between p-2 rounded-xl cursor-pointer transition-all ${
                    notifications.unreadMessages > 0 
                      ? 'bg-gradient-to-r from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 border border-blue-200 shadow-sm' 
                      : 'hover:bg-slate-50 dark:bg-slate-800 border border-transparent'
                  }`}
                  onClick={() => navigate('/messagerie')}
                  data-testid="notif-messages"
                >
                  <span className={`text-sm flex items-center gap-2 ${notifications.unreadMessages > 0 ? 'text-blue-700 font-semibold' : 'text-slate-600 dark:text-slate-300'}`}>
                    <MessageSquare className={`h-4 w-4 ${notifications.unreadMessages > 0 ? 'text-blue-600' : 'text-slate-400 dark:text-slate-500'}`} />
                    Messages non lus
                  </span>
                  <span className={`text-sm font-bold px-2 py-0.5 rounded-lg ${
                    notifications.unreadMessages > 0 
                      ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-sm' 
                      : 'text-slate-400 dark:text-slate-500'
                  }`}>
                    {notifications.unreadMessages}
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Quick Access Menu */}
        <div>
          <h2 className="text-lg font-bold text-gray-800 dark:text-slate-100 mb-2" style={{ fontFamily: 'Space Grotesk' }}>
            Accès rapide
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {(user?.permission_overrides?.view_calendar ?? true) && (
              <Card
                className="card-hover cursor-pointer transition-all"
                onClick={() => setShowCalendarModal(true)}
                data-testid="dashboard-card-calendar"
              >
                <CardHeader className="p-4">
                  <div className="w-9 h-9 bg-teal-500 rounded-lg flex items-center justify-center mb-2">
                    <CalendarDays className="h-4 w-4 text-white" />
                  </div>
                  <CardTitle>
                    {new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
                  </CardTitle>
                  <CardDescription>
                    {todayCalendarEvents.length === 0
                      ? "Aucun rendez-vous aujourd'hui"
                      : todayCalendarEvents.slice(0, 3).map(ev => `${ev.time ? ev.time + ' ' : ''}${ev.title}`).join(' · ')}
                    {todayCalendarEvents.length > 3 && ` (+${todayCalendarEvents.length - 3})`}
                  </CardDescription>
                  {nextEvent && (
                    <p className="text-xs text-teal-600 dark:text-teal-400 font-medium mt-2">
                      À venir : {formatUpcoming(nextEvent)}
                    </p>
                  )}
                </CardHeader>
              </Card>
            )}
            {todayReportSite && (
              <Card
                className="card-hover cursor-pointer transition-all"
                onClick={() => navigate('/reporting')}
                data-testid="dashboard-card-reporting"
              >
                <CardHeader className="p-4">
                  <div className="w-9 h-9 bg-orange-500 rounded-lg flex items-center justify-center mb-2">
                    <BarChart3 className="h-4 w-4 text-white" />
                  </div>
                  <CardTitle>Reporting du jour</CardTitle>
                  <CardDescription>
                    {todayReport ? (
                      <>
                        {todayReport.incendie_rondes + Object.values(todayReport.rondes_par_boutique || {}).reduce((a, b) => a + b, 0)} rondes ·{' '}
                        {(todayReport.interventions_galerie?.length || 0) + (todayReport.interventions_exterieur?.length || 0) + (todayReport.interventions_parking?.length || 0)} interventions ·{' '}
                        {todayReport.sst_interventions} secours à personne
                      </>
                    ) : (
                      `Pas encore rempli aujourd'hui pour ${todayReportSite.name}`
                    )}
                  </CardDescription>
                </CardHeader>
              </Card>
            )}
            {menuItems.map((item, index) => (
              <Card 
                key={index}
                className="card-hover cursor-pointer transition-all"
                onClick={() => navigate(item.path)}
                data-testid={`dashboard-card-${item.path.substring(1)}`}
              >
                <CardHeader className="p-4">
                  <div className={`w-9 h-9 ${item.color} rounded-lg flex items-center justify-center mb-2`}>
                    <item.icon className="h-4 w-4 text-white" />
                  </div>
                  <CardTitle>{item.title}</CardTitle>
                  <CardDescription>{item.description}</CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>

        {/* Contrôle des agents - réservé chef de poste et admin */}
        {(user?.role === 'admin' || user?.role === 'chef_poste') && (
          <AgentControlPanel sites={sites} user={user} />
        )}

        {/* Exports & Rapports - réservé aux admins, ou à qui la permission a été accordée.
            L'export Excel mensuel a été déplacé dans la Main Courante (section
            "période personnalisée"), pour exporter une plage de dates au choix
            plutôt qu'un mois calendaire fixe. */}
        {(user?.role === 'admin' || user?.permission_overrides?.send_daily_reports) && (
          <div>
            <h2 className="text-lg font-bold text-gray-800 dark:text-slate-100 mb-2" style={{ fontFamily: 'Space Grotesk' }}>
              Exports & Rapports
            </h2>
            <DailyReportPDF sites={sites} user={user} />
          </div>
        )}

        {/* Rapport de ronde : le chef de poste (ou admin) choisit à la main
            les événements de la journée à inclure dans un rapport PDF distinct
            du rapport de main courante complet. */}
        {(user?.role === 'admin' || user?.role === 'chef_poste') && (
          <div>
            <h2 className="text-lg font-bold text-gray-800 dark:text-slate-100 mb-2" style={{ fontFamily: 'Space Grotesk' }}>
              Rapport de ronde
            </h2>
            <RondeReportPDF sites={sites} />
          </div>
        )}
      </div>

      {/* Modal liste des sites */}
      {showSitesModal && (
        <div 
          className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
          onClick={() => setShowSitesModal(false)}
        >
          <div 
            className="bg-white dark:bg-slate-800 rounded-lg w-full max-w-2xl max-h-[85vh] overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b bg-gray-50 dark:bg-slate-800">
              <div className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-green-600" />
                <h3 className="font-semibold text-lg">Sites Assignés ({sites.length})</h3>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowSitesModal(false)}
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
            
            {/* Liste des sites */}
            <div className="overflow-auto p-4" style={{ maxHeight: 'calc(85vh - 70px)' }}>
              {sites.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-slate-400">
                  <MapPin className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>Aucun site assigné</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {sites.map((site) => (
                    <div 
                      key={site.id}
                      className="p-4 bg-gray-50 dark:bg-slate-800 rounded-lg border hover:bg-gray-100 dark:bg-slate-700 transition-colors cursor-pointer"
                      onClick={() => {
                        setShowSitesModal(false);
                        navigate('/main-courante');
                      }}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center shrink-0">
                          <MapPin className="h-5 w-5 text-green-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-gray-900 dark:text-slate-100">{site.name}</h4>
                          <p className="text-sm text-gray-600 dark:text-slate-300 mt-1">{site.address}</p>
                          {site.auto_pdf_enabled && (
                            <span className="inline-block mt-2 text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                              Rapport PDF automatique activé
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {user?.role === 'admin' && (
                <div className="mt-4 pt-4 border-t">
                  <Button 
                    className="w-full btn-primary"
                    onClick={() => {
                      setShowSitesModal(false);
                      navigate('/admin');
                    }}
                  >
                    <Users className="h-4 w-4 mr-2" />
                    Gérer les sites (Admin)
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Calendrier complet (vue mensuelle), ouvert depuis la carte du jour */}
      <Dialog open={showCalendarModal} onOpenChange={(open) => { setShowCalendarModal(open); if (!open) loadTodayEvents(); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Calendrier</DialogTitle>
          </DialogHeader>
          <CalendarWidget user={user} sites={sites} />
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Dashboard;
